import {
  ajouterMois,
  agregerCote,
  coutExpositionSchema,
  etatDormance,
  formatOeuvre,
  reglagesPilotageSchema,
  type CotePilotage,
  type CoutExposition,
  type DormantePilotage,
  type ExpositionPilotage,
  type ModuleAddon,
  type VentePilotage,
} from '@artosera/shared';
import {
  DEMO_ARTISTES,
  DEMO_DEMANDES,
  DEMO_EXPOSITIONS,
  DEMO_FACTURES,
  DEMO_MOUVEMENTS,
  DEMO_OEUVRES,
  DEMO_OFFRES,
  DEMO_VIEWING_ROOMS,
} from './demoFixtures.js';
import { ApiError } from './errors.js';

export const ADDONS_DEMONSTRATION = [
  'pilotage',
  'assistant-recherche',
] as const satisfies readonly ModuleAddon[];
let moisDormance = 12;
const couts: CoutExposition[] = [
  {
    id: 'cout-pilotage-1',
    expositionId: 'exposition-3',
    categorie: 'stand',
    libelle: 'Location du stand B12',
    montantCentimes: 400_000,
    devise: 'EUR',
    date: '2026-04-02',
    fournisseur: 'Foire de la Cimaise',
    note: '',
  },
  {
    id: 'cout-pilotage-2',
    expositionId: 'exposition-3',
    categorie: 'transport',
    libelle: 'Transport aller-retour',
    montantCentimes: 50_000,
    devise: 'EUR',
    date: '2026-04-06',
    fournisseur: 'Transports exemple',
    note: '',
  },
];

// Les éditions ont déjà des exemplaires vendus dans les fixtures.
const ventesFoire: VentePilotage[] = [
  { oeuvreId: 'oeuvre-5', date: '2026-04-04T00:00:00.000Z' },
  { oeuvreId: 'oeuvre-6', date: '2026-08-14T00:00:00.000Z' },
].map(({ oeuvreId, date }) => {
  const o = DEMO_OEUVRES.find((o) => o.id === oeuvreId)!;
  return {
    id: `vente-demo-${o.id}`,
    pieceId: o.id,
    nature: 'vente_simple',
    numero: o.numeroInventaire,
    oeuvreId: o.id,
    artisteId: o.artisteId,
    titre: o.titre,
    date,
    devise: 'EUR',
    caHtCentimes: o.prixPublicCentimes!,
    quantite: 1,
    prixHtCentimes: o.prixPublicCentimes!,
    prixPublicCentimes: null,
    remisePdb: null,
    annee: 2026,
    technique: o.technique!.fr,
    ...formatOeuvre({ type: o.type, dimensions: o.dimensions ?? undefined }),
    partArtisteCentimes: o.prixArtisteCentimes!,
  };
});
const ventes: VentePilotage[] = DEMO_FACTURES.filter(
  (f) => f.type === 'facture' && (f.etat === 'emise' || f.etat === 'payee'),
)
  .flatMap<VentePilotage>((f) =>
    f.lignes.flatMap((l, rang) => {
      const o = DEMO_OEUVRES.find((o) => o.id === l.oeuvreId);
      if (!o) return [];
      return [
        {
          id: `${f.id}:${rang}`,
          pieceId: f.id,
          nature: 'facture' as const,
          numero: f.numero!,
          oeuvreId: o.id,
          artisteId: o.artisteId,
          titre: o.titre,
          date: f.emiseLe!,
          devise: f.devise,
          caHtCentimes: l.prixUnitaireNetCentimes * l.quantite,
          quantite: l.quantite,
          prixHtCentimes: l.prixUnitaireNetCentimes,
          prixPublicCentimes: null,
          remisePdb: null,
          annee: new Date(f.emiseLe!).getUTCFullYear(),
          technique: o.technique?.fr ?? '',
          ...formatOeuvre({ type: o.type, dimensions: o.dimensions ?? undefined }),
          partArtisteCentimes: o.prixArtisteCentimes! * l.quantite,
        },
      ];
    }),
  )
  .concat(ventesFoire);

function expositionsDemo(): ExpositionPilotage[] {
  const maintenant = new Date();
  return DEMO_EXPOSITIONS.map((e) => ({
    id: e.id,
    titre: e.titre.fr,
    type: e.type,
    debutLe: e.debutLe,
    finLe: e.finLe,
    horizons: ([3, 6] as const).map((mois) => {
      const horizon = ajouterMois(new Date(e.finLe), mois);
      horizon.setUTCHours(23, 59, 59, 999);
      const observeJusquAu = new Date(
        Math.min(horizon.getTime(), maintenant.getTime()),
      ).toISOString();
      const lignes = ventesFoire.filter((v) => e.id === 'exposition-3' && v.date <= observeJusquAu);
      const caHtCentimes = lignes.reduce((s, v) => s + v.caHtCentimes, 0);
      const partArtisteCentimes = lignes.reduce((s, v) => s + (v.partArtisteCentimes ?? 0), 0);
      const coutsCentimes = couts
        .filter((c) => c.expositionId === e.id && c.date <= observeJusquAu.slice(0, 10))
        .reduce((s, c) => s + c.montantCentimes, 0);
      const margeCentimes = caHtCentimes - partArtisteCentimes;
      return {
        mois,
        jusquAu: horizon.toISOString(),
        observeJusquAu,
        complet: horizon <= maintenant,
        ventes: structuredClone(lignes),
        bilans: [
          {
            devise: 'EUR',
            caHtCentimes,
            partArtisteCentimes,
            margeCentimes,
            coutsCentimes,
            resultatCentimes: margeCentimes - coutsCentimes,
            ventesSansPart: 0,
          },
        ],
      };
    }),
  }));
}

export function dormantesDemo(): { moisDormance: number; oeuvres: DormantePilotage[] } {
  const maintenant = new Date();
  const oeuvres = DEMO_OEUVRES.filter((o) => o.statut === 'disponible').flatMap((o) => {
    const expositions = DEMO_EXPOSITIONS.filter(
      (e) => e.oeuvres.includes(o.id) && new Date(e.debutLe) <= maintenant,
    );
    const offres = DEMO_OFFRES.filter((f) => f.lignes.some((l) => l.oeuvreId === o.id));
    const demandes = DEMO_DEMANDES.filter((d) => d.oeuvreId === o.id);
    const dates = [
      ...expositions.map(
        (e) => new Date(Math.min(new Date(e.finLe).getTime(), maintenant.getTime())),
      ),
      ...offres.flatMap((f) => [new Date(f.creeeLe), new Date(f.modifieeLe)]),
      ...demandes.flatMap((d) => [
        new Date(d.recueLe),
        ...(d.repondueLe ? [new Date(d.repondueLe)] : []),
      ]),
      ...DEMO_MOUVEMENTS.filter((m) => m.lignes.some((l) => l.oeuvreId === o.id)).flatMap((m) => [
        new Date(m.creeLe),
        new Date(m.modifieLe),
      ]),
      ...DEMO_VIEWING_ROOMS.filter(
        (s) => s.oeuvres.some((l) => l.id === o.id) && s.ouvertures.derniereLe,
      ).map((s) => new Date(s.ouvertures.derniereLe!)),
    ].filter((d) => d <= maintenant);
    const etat = etatDormance(
      {
        statut: o.statut,
        creeLe: new Date(o.creeLe),
        dernieresDates: dates,
        contactsInteresses: demandes.some((d) => d.contactId !== null),
        dejaExposee: expositions.length > 0,
        offresRefusees: offres.some((f) => f.refuseeLe !== null),
        artisteRepresente:
          DEMO_ARTISTES.find((a) => a.id === o.artisteId)?.representation.representee === true,
      },
      moisDormance,
      maintenant,
    );
    if (!etat.dormante) return [];
    const d = etat.derniereActiviteLe;
    return [
      {
        id: o.id,
        titre: o.titre,
        artisteId: o.artisteId,
        artisteNom: o.artisteNom,
        derniereActiviteLe: d.toISOString(),
        ancienneteMois:
          (maintenant.getUTCFullYear() - d.getUTCFullYear()) * 12 +
          maintenant.getUTCMonth() -
          d.getUTCMonth() -
          Number(maintenant.getUTCDate() < d.getUTCDate()),
        decision: etat.decision,
      },
    ];
  });
  return { moisDormance, oeuvres };
}

export function reponseDemoPilotage(
  chemin: string,
  params: URLSearchParams,
  methode: string,
  corps?: unknown,
): unknown | undefined {
  if (chemin === '/pilotage/expositions' && methode === 'GET')
    return {
      expositions: expositionsDemo().filter(
        (e) =>
          (!params.get('du') || e.finLe.slice(0, 10) >= params.get('du')!) &&
          (!params.get('au') || e.debutLe.slice(0, 10) <= params.get('au')!),
      ),
    };
  const exposition = /^\/pilotage\/expositions\/([^/]+)(?:\/couts(?:\/([^/]+))?)?$/.exec(chemin);
  if (exposition) {
    const e = expositionsDemo().find((e) => e.id === exposition[1]);
    if (!e) throw new ApiError(404, 'ressource.introuvable', 'Exposition introuvable.');
    if (methode === 'GET')
      return {
        exposition: e,
        couts: structuredClone(couts.filter((c) => c.expositionId === e.id)),
      };
    if (methode === 'POST' || methode === 'PUT') {
      const saisie = coutExpositionSchema.parse(corps);
      if (saisie.devise !== 'EUR')
        throw new ApiError(
          400,
          'validation.invalide',
          'La démonstration du pilotage utilise uniquement l’euro.',
        );
      if (methode === 'POST') {
        const cout = {
          ...saisie,
          id: `cout-${crypto.randomUUID()}`,
          expositionId: e.id,
        };
        couts.push(cout);
        return { cout: structuredClone(cout) };
      }
      const index = couts.findIndex((c) => c.id === exposition[2] && c.expositionId === e.id);
      if (index < 0) throw new ApiError(404, 'ressource.introuvable', 'Coût introuvable.');
      const cout = { ...saisie, id: exposition[2]!, expositionId: e.id };
      couts[index] = cout;
      return { cout: structuredClone(cout) };
    }
    if (methode === 'DELETE') {
      const index = couts.findIndex((c) => c.id === exposition[2] && c.expositionId === e.id);
      if (index < 0) throw new ApiError(404, 'ressource.introuvable', 'Coût introuvable.');
      couts.splice(index, 1);
      return null;
    }
  }
  if (chemin === '/pilotage/artistes' && methode === 'GET')
    return { artistes: DEMO_ARTISTES.map(({ id, nom }) => ({ id, nom })) };
  const artiste = /^\/pilotage\/artistes\/([^/]+)\/cote$/.exec(chemin);
  if (artiste && methode === 'GET') {
    const a = DEMO_ARTISTES.find((a) => a.id === artiste[1]);
    if (!a) throw new ApiError(404, 'ressource.introuvable', 'Artiste introuvable.');
    const lignes = ventes.filter((v) => v.artisteId === a.id && new Date(v.date) <= new Date());
    return {
      artiste: { id: a.id, nom: a.nom },
      ventes: structuredClone(lignes),
      parAnnee: agregerCote(lignes, 'annee'),
      parFormat: agregerCote(lignes, 'format'),
      parTechnique: agregerCote(lignes, 'technique'),
    } satisfies CotePilotage;
  }
  if (chemin === '/pilotage/dormantes' && methode === 'GET') {
    const resultat = dormantesDemo();
    return {
      ...resultat,
      oeuvres: resultat.oeuvres.filter(
        (o) =>
          (!params.get('artisteId') || o.artisteId === params.get('artisteId')) &&
          o.ancienneteMois >= Number(params.get('ancienneteMois') ?? 0),
      ),
    };
  }
  if (chemin === '/pilotage/reglages') {
    if (methode === 'PUT') moisDormance = reglagesPilotageSchema.parse(corps).moisDormance;
    if (methode === 'GET' || methode === 'PUT') return { moisDormance };
  }
  return undefined;
}
